import { RouteProp } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { useCollaborators } from '../../../../../hooks/api/useCollaborators';
import { getIntl } from '../../../../../utils/intl';
import { ABAC } from '../../../../../primary-record-types';
import { completelyRestrictedPermissions } from '../../../../../utils/evaluatePermissions';
import { Field } from 'formik';
import { useHasPermission, useIsProfileOwner } from '../../../../v2BaseComponents/Permissions/hooks';
import { ProfilesTabParamList, useGoBack, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { handleFormikSubmit } from '../../../../../utils/handleFormikSubmit';
import { useConfirmBackNavigationOnDirtyForm } from '../../../../shared/FormExitValidation';
import { CenteredFormRow, Form } from '../../../../v2BaseComponents/Form';
import Text from '../../../../v2BaseComponents/Text';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';
import { useLoadedUser } from '../../../../../hooks/api/useUser';
import useMedicalProfiles from '../../../../../hooks/api/useMedicalProfiles';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { PromptActions } from '../../../../v2BaseComponents/Prompt';
import { usePromptButton } from '../../../../shared/PromptButton';
import {
    isOrganizationCollaborator as getIsOrganizationCollaborator,
    isUserCollaborator,
} from '../../../../../utils/collaboratorUtils';
import AssociatedCareTeamMember from './AssociatedCareTeamMember';
import OrganizationCollaboratorView from './OrganizationCollaboratorView';
import CollaboratorHeader from './CollaboratorHeader';
import { FormStack } from '../../../../v2BaseComponents/Stack';
import { useRemoveOrganizationAsCollaborator } from '../../../../../hooks/api/useRemoveOrganizationAsCollaborator';
import useOrganization from '../../../../../hooks/api/useOrganization';
import useMedicalProfile from '../../../../../hooks/api/useMedicalProfile';
import useEvaluatedPermissions from '../../../../../hooks/api/useEvaluatedPermissions';

const intl = getIntl('profile-collaborator-details', {
    deleteCollaborator: 'Remove Access',
    updateCollaborator: 'Save',
    transferOwnership: 'Transfer Ownership',
    collaboratorDetails: 'Collaborator Details',
    removeSelfAsCollaborator: 'Remove Self As Collaborator',
    removeOrganizationAsCollaborator: 'Remove Organization As Collaborator',
    deleteOrganizationCollaboratorNotificationHeader:
        'Are you sure you want to remove this organization as a collaborator? Members of the organization will no longer have access to this Medical Profile.',
    deletePrompt: 'Remove Yourself as a Collaborator',
    deleteNotificationHeader: 'Are you sure you want to stop collaborating on this profile?',
    deleteButton: 'Yes',
    cancelButton: 'Cancel',
    removeCollaborator: 'Remove Collaborator',
    removeCollaboratorHeader:
        'Are you sure you want to remove this collaborator?  They will no longer have access to this Medical Profile.',
    invoices: 'Invoices',
});

export type CollaboratorScreenOwnProps = {};

export type CollaboratorScreenProps = CollaboratorScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'collaborator'>;
};

type FormValue = {
    appPermissions: ABAC.AppPermissions;
};

const CollaboratorScreen: React.FC<CollaboratorScreenProps> = ({
    route: {
        params: { collaboratorId, profileId },
    },
}) => {
    const { permissions } = useEvaluatedPermissions({ medicalProfileId: profileId });
    const canEditCollaborators = useHasPermission(profileId, 'collaborators', 'edit');
    const navigation = useProfilesTabNavigation();
    const goBack = useGoBack();
    const user = useLoadedUser({});
    useConfirmBackNavigationOnDirtyForm(navigation);
    const {
        isLoadingCollaborators,
        collaborators,
        isUpdatingCollaboratorPermissions,
        updateCollaboratorPermissions,
        deleteCollaborator,
        isDeletingCollaborator,
    } = useCollaborators({
        medicalProfileId: profileId,
        onDeleteCollaboratorSuccess: () => goBack(),
    });
    const { myOrganization } = useOrganization({});
    const { isRemovingSelfAsCollaborator, removeSelfAsCollaborator } = useMedicalProfiles({
        onRemoveSelfAsCollaboratorSuccess: () => navigation.navigate('root', undefined),
    });
    const { isLoadingProfile, profile } = useMedicalProfile({
        medicalProfileId: profileId,
    });
    const { removeOrganizationAsCollaborator, isRemovingOrganizationCollaborator } =
        useRemoveOrganizationAsCollaborator({
            onRemoveOrganizationAsCollaboratorSuccess: () => navigation.navigate('root', undefined),
        });
    const collaborator = (collaborators || []).find(
        (collaborator) => collaborator.medicalProfileCollaboratorId === collaboratorId,
    );
    const isProfileOwner = useIsProfileOwner(profileId);
    const isOrganizationCollaborator = !!(collaborator && getIsOrganizationCollaborator(collaborator));

    const initialValue = useMemo<FormValue>(() => {
        return {
            appPermissions: collaborator?.appPermissions || completelyRestrictedPermissions,
        };
    }, [collaborator]);

    const isLoading =
        isUpdatingCollaboratorPermissions ||
        isDeletingCollaborator ||
        isLoadingCollaborators ||
        !collaborator ||
        isLoadingProfile;
    const isDisabled = !canEditCollaborators || isLoading;

    const removeSelfButton = usePromptButton({
        renderPromptContents: (onClose) => (
            <MainContentContainer>
                <Text variant="body-1">{intl('deleteNotificationHeader')}</Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('deleteButton'),
                            onPress: () => {
                                removeSelfAsCollaborator(profileId);
                                onClose();
                            },
                            variant: 'alert',
                            testID: 'remove-self-as-collaborator-button',
                        },
                        {
                            title: intl('cancelButton'),
                            onPress: onClose,
                            variant: 'secondary',
                            testID: 'cancel-self-as-collaborator-button',
                        },
                    ]}
                />
            </MainContentContainer>
        ),
        promptTitle: intl('deletePrompt'),
    });

    const removeOrganizationCollaboratorButton = usePromptButton({
        renderPromptContents: (onClose) => (
            <MainContentContainer>
                <Text variant="body-1">{intl('deleteOrganizationCollaboratorNotificationHeader')}</Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('deleteButton'),
                            onPress: () => {
                                if (!collaborator || !getIsOrganizationCollaborator(collaborator)) return;
                                removeOrganizationAsCollaborator({
                                    organizationId: collaborator.organization.organizationId,
                                    medicalProfileId: profileId,
                                });
                                onClose();
                            },
                            variant: 'alert',
                            testID: 'remove-organization-as-collaborator-button',
                        },
                        {
                            title: intl('cancelButton'),
                            onPress: onClose,
                            variant: 'secondary',
                            testID: 'cancel-organization-as-collaborator-button',
                        },
                    ]}
                />
            </MainContentContainer>
        ),
        promptTitle: intl('removeOrganizationAsCollaborator'),
    });

    const removeCollaboratorButton = usePromptButton({
        renderPromptContents: (onClose) => (
            <MainContentContainer>
                <Text variant="body-1">{intl('removeCollaboratorHeader')}</Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('deleteButton'),
                            onPress: () => {
                                deleteCollaborator(collaboratorId);
                                onClose();
                            },
                            variant: 'alert',
                            testID: 'confirm-remove-collaborator-button',
                        },
                        {
                            title: intl('cancelButton'),
                            onPress: onClose,
                            variant: 'secondary',
                            testID: 'cancel-remove-collaborator-button',
                        },
                    ]}
                />
            </MainContentContainer>
        ),
        promptTitle: intl('removeCollaborator'),
    });

    return (
        <>
            <ProfileHeaderContainer
                profileId={profileId}
                bottomBorder={!isLoading && !isOrganizationCollaborator}
                omitChatButton
            >
                <AnimateLoadedContent isLoading={isLoading}>
                    {() => (
                        <Form
                            title={isOrganizationCollaborator ? '' : intl('collaboratorDetails')}
                            actionContainerSubmit
                            saveText={intl('updateCollaborator')}
                            initialValues={initialValue}
                            enableReinitialize
                            fillContainer
                            onCancel={goBack}
                            cancelButtonVariant="secondary"
                            contentContainerStyle={isOrganizationCollaborator ? { padding: 0 } : undefined}
                            onSubmit={(values, helpers) => {
                                updateCollaboratorPermissions({
                                    collaboratorId,
                                    update: { appPermissions: values.appPermissions },
                                });
                                handleFormikSubmit(values, helpers);
                            }}
                            nonDirtyActions={[
                                {
                                    icon: 'UserShield',
                                    title: intl('transferOwnership'),
                                    onPress: () => {
                                        navigation.navigate('add-transfer-request', {
                                            profileId,
                                            collaboratorId,
                                        });
                                    },
                                    show: isProfileOwner && collaborator && isUserCollaborator(collaborator),
                                    testID: 'transfer-ownership-button',
                                },
                                {
                                    icon: 'UserX',
                                    title: intl('deleteCollaborator'),
                                    onPress: () => removeCollaboratorButton.onPress(),
                                    show:
                                        collaborator &&
                                        canEditCollaborators &&
                                        !(
                                            isUserCollaborator(collaborator) &&
                                            collaborator?.user.userId === user.user.userId
                                        ),
                                    testID: 'delete-collaborator-button',
                                },
                                {
                                    icon: 'UserMinus',
                                    title: intl('removeSelfAsCollaborator'),
                                    variant: 'alert',
                                    onPress: removeSelfButton.onPress,
                                    show:
                                        collaborator &&
                                        isUserCollaborator(collaborator) &&
                                        collaborator?.user.userId === user.user.userId,
                                    testID: 'remove-self-as-collaborator-prompt-button-collaborator-screen',
                                    isDisabled: isRemovingSelfAsCollaborator,
                                },
                                {
                                    icon: 'UserMinus',
                                    title: intl('removeOrganizationAsCollaborator'),
                                    variant: 'alert',
                                    onPress: removeOrganizationCollaboratorButton.onPress,
                                    show:
                                        user.user.type === 'professional' &&
                                        (myOrganization?.role === 'admin' || myOrganization?.role === 'owner') &&
                                        !profile?.isProvisionedMedicalProfile,
                                    testID: 'remove-organization-as-collaborator-prompt-button',
                                    isDisabled: isRemovingOrganizationCollaborator,
                                },
                            ]}
                        >
                            {() =>
                                isOrganizationCollaborator ? (
                                    <OrganizationCollaboratorView
                                        medicalProfileId={profileId}
                                        collaboratorId={collaboratorId}
                                        isDisabled={isDisabled}
                                        collaborator={collaborator}
                                        initialAppPermissions={initialValue.appPermissions}
                                    />
                                ) : (
                                    <FormStack>
                                        <CollaboratorHeader
                                            medicalProfileId={profileId}
                                            collaboratorId={collaboratorId}
                                            collaborator={collaborator}
                                        />
                                        <AssociatedCareTeamMember
                                            medicalProfileId={profileId}
                                            associatedCareTeamMember={collaborator?.associatedCareTeamMember}
                                        />
                                        <CenteredFormRow>
                                            <Field
                                                component={FormikInputs.PermissionsInput}
                                                name="appPermissions"
                                                isDisabled={isDisabled}
                                                mostPermissiveAppPermissions={permissions.permissions}
                                                initialAppPermissions={initialValue.appPermissions}
                                            />
                                        </CenteredFormRow>
                                    </FormStack>
                                )
                            }
                        </Form>
                    )}
                </AnimateLoadedContent>
            </ProfileHeaderContainer>
            {removeSelfButton.Prompt}
            {removeOrganizationCollaboratorButton.Prompt}
            {removeCollaboratorButton.Prompt}
        </>
    );
};

export default React.memo(CollaboratorScreen);
